import { MIDDLE_C, TICKS_PER_LOOP } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import React, { FC } from 'react'
import { useDrop } from 'react-dnd'
import { GRAY_COLORS } from '../../../lib/stitches'
import { useTreeState } from '../../../stores/Root'
import { IDrumStemStore } from '../../../stores/song/stem/DrumStemStore'
import { IStemStore } from '../../../stores/song/StemStore'
import { Row, StyledScrollable } from '../../core/Components'
import LoadingAnimation from '../../core/LoadingAnimation'
import { FIRST_COLUMN_WIDTH } from '../../studio/Studio'
import RandomizeButton from '../controls/RandomizeButton'
import VolumeSlider from '../controls/VolumeSlider'
import { getDrumStemSample, getDrumStemSampleCategory, StudioDraggableTypes } from '../StudioUtils'

export const DrumSequencerHeader: FC<any> = observer(
  ({
    isSelected,
    randomize,
    height,
    drumIndex,
    selectedDrumTrack,
    setSelectedDrumTrack,
    stemIndex,
    sampleWithEffects
  }) => {
    const [isHover, setIsHover] = React.useState(false)
    const { studio } = useTreeState()
    const stem: IStemStore = studio.song.stems[stemIndex]

    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: StudioDraggableTypes.SAMPLE,
        drop: ({ sample }) => studio.updateDrumStemSample(stemIndex, sample, drumIndex),
        collect: (monitor) => ({
          isOver: !!monitor.isOver()
        })
      }),
      []
    )

    return (
      <div
        className="d-inline-flex align-items-center justify-content-between unselectable fw-medium"
        style={{
          minWidth: FIRST_COLUMN_WIDTH || '100%',
          maxWidth: FIRST_COLUMN_WIDTH || '100%',
          backgroundColor: isOver
            ? 'var(--studio-light)'
            : isSelected
            ? 'var(--studio-light)'
            : GRAY_COLORS.gray01,
          paddingLeft: '25px',
          height: height,
          minHeight: height,
          verticalAlign: 'middle'
        }}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={() => {
          studio.playSampleWithEffects(sampleWithEffects, stem.getStemGain())
          setSelectedDrumTrack(drumIndex)
        }}
        ref={drop}
      >
        <div
          className="track-header-text ellipsis-overflow"
          title={getDrumStemSample(stem, drumIndex) || ''}
        >
          <div className="track-header-text ellipsis-overflow">
            {getDrumStemSampleCategory(stem, drumIndex) || <LoadingAnimation small />}
          </div>
          <div className="track-details-text ellipsis-overflow">
            {getDrumStemSample(stem, drumIndex) || <LoadingAnimation small />}
          </div>
        </div>

        <div className="d-flex h-100">
          <div className="d-inline-flex align-items-center">
            {!studio.song.stems[stemIndex].isRemixed && (
              <RandomizeButton randomize={randomize} className="me-2" />
            )}
            <VolumeSlider
              value={(stem as any).getDrumGain(drumIndex)}
              setGain={(gain) => {
                studio.song.updateDrumStemGain(stemIndex, drumIndex, gain)
              }}
              width="80px"
            />
          </div>
          <div className="h-100 d-column-flex" style={{ width: '24px', marginLeft: '10px' }}>
            {isHover && !stem.isRemixed && (
              <img
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  if (drumIndex <= selectedDrumTrack && selectedDrumTrack > 0) {
                    setSelectedDrumTrack(Math.max(selectedDrumTrack - 1, 0))
                  }
                  ;(stem as any).removeDrumSample(drumIndex)
                }}
                src="/static/icons/close_black_24dp.svg"
                style={{ width: '14px' }}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
)

const DrumSequencerNote: FC<any> = observer(
  ({
    isSelected,
    isActive,
    isCursor,
    setIsSelected,
    colNumber,
    noteColor,
    noteCount,
    backgroundColor,
    rightMargin,
    rowSelected
  }) => {
    return (
      <div
        style={{
          width: String(100.0 / noteCount) + '%',
          backgroundColor: isSelected ? noteColor : rowSelected ? noteColor : backgroundColor,
          borderRadius: '2px',
          height: '95%',
          marginRight: rightMargin,
          filter: rowSelected && !isSelected && 'opacity(40%)'
        }}
        onMouseDown={() => setIsSelected(!isSelected)}
        onMouseOver={(e) => {
          if (e.buttons === 1 || e.buttons === 3) setIsSelected(!isSelected)
        }}
      />
    )
  }
)

const DrumSequencerRow: FC<any> = observer(
  ({
    count,
    noteColor,
    notes,
    sampleWithEffects,
    height,
    stemIndex,
    drumIndex,
    randomize,
    selectedDrumTrack,
    setSelectedDrumTrack,
    immutable
  }) => {
    const { studio } = useTreeState()
    const song = studio.song
    const stem = song.stems[stemIndex]

    return (
      <div style={{ height: height, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <DrumSequencerHeader
          height={height}
          randomize={randomize}
          drumIndex={drumIndex}
          isSelected={drumIndex === selectedDrumTrack}
          selectedDrumTrack={selectedDrumTrack}
          setSelectedDrumTrack={setSelectedDrumTrack}
          stemIndex={stemIndex}
          sampleWithEffects={sampleWithEffects}
        />
        {notes &&
          [...Array(count)].map((item, index) => {
            const noteDuration = Math.round(TICKS_PER_LOOP / count)
            const noteStart = index * noteDuration
            const noteAtLocation = notes.notes.find((note) => note.start === noteStart)
            return (
              <DrumSequencerNote
                key={index}
                backgroundColor="var(--gray-3)"
                colspan={64 / count}
                colNumber={index}
                rightMargin={
                  [0.25, 0.5, 0.75].includes(+((index + 1) / count).toFixed(2)) ? '12px' : '3px'
                }
                isCursor={
                  // Puts the cursor on the current note -- this also checks
                  // to make sure that two notes that are next to one (and round to the same number)
                  // aren't both considered the cursor
                  Math.floor((studio.playhead % 64) / 2) === Math.floor((index * 32) / count) &&
                  Math.floor((studio.playhead % 64) / 2) !==
                    Math.floor((Math.max(index - 1, 0) * 32) / count)
                }
                noteColor={noteColor}
                noteCount={count}
                isSelected={notes && noteAtLocation}
                isActive={true}
                setIsSelected={(selected) => {
                  if (immutable) {
                    return
                  }
                  if (!selected) {
                    noteAtLocation && notes.removeNotes([noteAtLocation.uniqueId])
                  } else {
                    notes.addNote({ start: noteStart, duration: noteDuration - 1, note: MIDDLE_C })
                  }
                }}
                rowSelected={drumIndex === selectedDrumTrack && !immutable}
              />
            )
          })}
      </div>
    )
  }
)

const DrumSequencerBeatCounter: FC<any> = observer(({ style, noteColor }) => {
  const { studio } = useTreeState()
  const currDot = Math.floor((studio.playheadVisiblePositionInTicks % TICKS_PER_LOOP) / 48)

  return (
    <Row
      className="sticky-top"
      style={{
        width: '100%',
        height: '30px',
        backgroundColor: GRAY_COLORS.gray01,
        ...style
      }}
      verticalCenter
    >
      {Array(32)
        .fill(null)
        .map((_, ind) => (
          <Row
            css={{
              width: String(100.0 / 32) + '%',
              marginRight: [8, 16, 24].includes(ind + 1) ? '12px' : '3px',
              height: '100%',
              cursor: 'pointer'
            }}
            onMouseDown={(e) => studio.engageTimeline()}
            onMouseUp={(e) => studio.disengageTimeline()}
            onClick={() => {
              const sequencerPosition = studio.selectedSection * 64 + ind * 2
              studio.userSetPlayhead(sequencerPosition)
            }}
            key={ind}
            verticalCenter
            horizontalCenter
          >
            <div
              className="height-equals-width"
              style={{
                width: `calc(min(${100}%, 10px))`,
                backgroundColor:
                  currDot === ind && studio.currentSection === studio.selectedSection
                    ? noteColor
                    : 'var(--gray-3)',
                borderRadius: '50%'
              }}
            />
          </Row>
        ))}
    </Row>
  )
})

const DrumSequencer: FC<any> = ({
  noteColor,
  stemIndex,
  selectedDrumTrack,
  setSelectedDrumTrack
}) => {
  const { studio } = useTreeState()
  const stem = studio.song.stems[stemIndex]
  const loop = stem.getLoopAtSectionOrNull(studio.selectedSection)
  const loopIndex = studio.song.getLoopIndex(stemIndex, studio.selectedSection)
  const drumSamples = (stem as IDrumStemStore).drumSamples

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: [StudioDraggableTypes.SAMPLE],
      drop: (item: any) => {
        studio.addDrumSampleToStem(item.sample, stemIndex)
        console.log('dropped a new sample to drum sequencer')
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    []
  )

  return (
    <StyledScrollable
      style={{
        flex: 1,
        height: '100px',
        overflowY: 'scroll'
      }}
    >
      <DrumSequencerBeatCounter style={{ paddingLeft: FIRST_COLUMN_WIDTH }} noteColor={noteColor} />
      {loop &&
        loop.drumLoop.subLoops.map((subLoop, drumIndex) => (
          <DrumSequencerRow
            drumIndex={drumIndex}
            count={subLoop.lengthOfLoop}
            noteColor={stem.isRemixed ? 'var(--piano-roll-immutable-gray)' : noteColor}
            totalNoteValues={1}
            notes={subLoop.notes}
            sampleWithEffects={drumSamples[drumIndex]}
            height={'54px'}
            stemIndex={stemIndex}
            randomize={() => {
              studio.randomizeDrumSubLoop(stemIndex, loopIndex - 1, drumIndex)
            }}
            setSelectedDrumTrack={setSelectedDrumTrack}
            selectedDrumTrack={selectedDrumTrack}
            immutable={stem.isRemixed}
            key={drumIndex}
          />
        ))}
      {loop && !stem.isRemixed && (
        <Row
          className="fw-medium"
          style={{
            height: '54px',
            width: '100%',
            backgroundColor: isOver ? 'var(--studio-light)' : 'inherit'
          }}
          ref={drop}
          verticalCenter
          unselectable
        >
          <div style={{ paddingLeft: '25px' }}>+ Drop samples</div>
        </Row>
      )}
    </StyledScrollable>
  )
}

export default observer(DrumSequencer)
